import React, { useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ProspectionPRCarteView.css';

const DEFAULT_CENTER = [14.6415, -61.0242];
const DEFAULT_ZOOM   = 12;
const MAX_ZOOM       = 21;   // zoom max d'affichage
const MAX_NATIVE     = 19;   // dernier niveau de tuiles réel ; au-delà, Leaflet agrandit (pas de gris)

// Orthophoto IGN (Géoplateforme) — gratuit, sans clé, haute résolution sur les territoires
// français (DOM inclus). Même source que Gestion Parking.
const IGN_ORTHO_URL =
  'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
  '&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/jpeg' +
  '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';

/* ── Icône SVG colorée (réutilisée comme icône Leaflet) ─────────────── */
const iconCache = {};
function svgIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38">
    <filter id="sh"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/></filter>
    <path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 22 16 22S32 26 32 16C32 7.163 24.837 0 16 0z"
          fill="${color}" filter="url(#sh)"/>
    <circle cx="16" cy="16" r="8" fill="white" opacity="0.9"/>
  </svg>`;
  const icon = L.icon({
    iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    iconSize:   [32, 38],
    iconAnchor: [16, 38],
    popupAnchor: [0, -34],
  });
  iconCache[color] = icon;
  return icon;
}

/* Ajuste la vue aux marqueurs — une seule fois (au montage), vue stable ensuite */
function FitBounds({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    if (points.length > 1) {
      map.fitBounds(points, { padding: [48, 48] });
    } else {
      map.setView(points[0], 17);
    }
    done.current = true;
  }, [points, map]);
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════
   Composant principal — calqué sur ParkingCarteView.js
═══════════════════════════════════════════════════════════════════════ */
export default function ProspectionPRCarteView({ clients, onOpenClient, pinColor }) {
  const color = pinColor || '#0891b2';

  const geoClients = useMemo(
    () => clients.filter(c =>
      c.latitude != null && c.longitude != null &&
      !isNaN(Number(c.latitude)) && !isNaN(Number(c.longitude))
    ),
    [clients]
  );

  const points = useMemo(
    () => geoClients.map(c => [Number(c.latitude), Number(c.longitude)]),
    [geoClients]
  );

  const totalFiltres = clients.length;
  const totalGeo     = geoClients.length;
  const totalSansGeo = totalFiltres - totalGeo;

  return (
    <div className="ppr-cv-wrapper">

      {/* ── Bandeau info ── */}
      <div className="ppr-cv-infobar">
        <span className="ppr-cv-count">
          📍 <strong>{totalGeo}</strong> client{totalGeo !== 1 ? 's' : ''} géolocalisé{totalGeo !== 1 ? 's' : ''}
          {totalSansGeo > 0 && (
            <span className="ppr-cv-nogeo"> · {totalSansGeo} sans coordonnées</span>
          )}
        </span>
      </div>

      {/* ── Carte ── */}
      <div className="ppr-cv-map-container">
        {geoClients.length === 0 ? (
          <div className="ppr-cv-empty">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucun client géolocalisé</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {totalFiltres === 0
                ? 'Aucun client ne correspond aux filtres actifs.'
                : `${totalFiltres} client${totalFiltres !== 1 ? 's' : ''} filtré${totalFiltres !== 1 ? 's' : ''}, mais aucun n'a de latitude/longitude renseignée.`}
            </div>
          </div>
        ) : (
          <MapContainer
            className="ppr-cv-gmap"
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            maxZoom={MAX_ZOOM}
            scrollWheelZoom
            zoomControl
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satellite (IGN)">
                <TileLayer
                  url={IGN_ORTHO_URL}
                  attribution='Orthophotos &copy; IGN / Géoplateforme'
                  maxZoom={MAX_ZOOM}
                  maxNativeZoom={MAX_NATIVE}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Plan (OpenStreetMap)">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; contributeurs OpenStreetMap'
                  maxZoom={MAX_ZOOM}
                  maxNativeZoom={MAX_NATIVE}
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            <FitBounds points={points} />

            {geoClients.map(client => (
              <Marker
                key={client.id}
                position={[Number(client.latitude), Number(client.longitude)]}
                icon={svgIcon(color)}
                title={client.nom || ''}
              >
                <Popup>
                  <div className="ppr-cv-popup-inner">
                    <div className="ppr-cv-popup-header">
                      <span className="ppr-cv-popup-nom">{client.nom || '—'}</span>
                    </div>

                    {client.adresse && (
                      <div className="ppr-cv-popup-row">📍 <span>{client.adresse}</span></div>
                    )}

                    {client.code_client_datacar && (
                      <div className="ppr-cv-popup-row">🔖 <span>{client.code_client_datacar}</span></div>
                    )}

                    {client.concession_nom && (
                      <div className="ppr-cv-popup-row ppr-cv-popup-concession">🏢 {client.concession_nom}</div>
                    )}

                    {client.date_derniere_visite && (
                      <div className="ppr-cv-popup-row ppr-cv-popup-concession">
                        🗓️ Dernière visite : {new Date(client.date_derniere_visite).toLocaleDateString('fr-FR')}
                      </div>
                    )}

                    {(client.contact_prenom || client.contact_nom) && (
                      <div className="ppr-cv-popup-row">
                        👤 <span>{[client.contact_prenom, client.contact_nom].filter(Boolean).join(' ')}</span>
                      </div>
                    )}
                    {client.contact_telephone && (
                      <div className="ppr-cv-popup-row">📞 <span>{client.contact_telephone}</span></div>
                    )}
                    {client.contact_email && (
                      <div className="ppr-cv-popup-row">✉️ <span>{client.contact_email}</span></div>
                    )}

                    <button
                      className="ppr-cv-popup-btn"
                      onClick={() => onOpenClient(client)}
                    >
                      ✏️ Modifier
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
